import React, { useState, useEffect } from 'react';
import { staffApi, rolesApi, clinicsApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX, HiOutlineEye, HiOutlineEyeOff, HiOutlineTrash } from 'react-icons/hi';
import { formatValidationErrors } from '../../utils/errorHandler';
import { todayYmdIST } from '../../utils/timezoneDisplay';
import { getCached } from '../../services/lookupCache';
import SalaryIncrements from '../SalaryIncrements';

// Capitalize role name for display (backend stores lowercase e.g. "doctor")
const formatRoleLabel = (name = '') =>
  name.charAt(0).toUpperCase() + name.slice(1);

const parseClinicIds = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((id) => String(id));
  if (typeof raw === 'number') return [String(raw)];
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      if (Array.isArray(parsed)) return parsed.map((id) => String(id));
      return [String(parsed)];
    } catch {
      return [t];
    }
  }
  if (typeof raw === 'object' && raw.type === 'Buffer' && Array.isArray(raw.data)) {
    try {
      const parsed = JSON.parse(String.fromCharCode(...raw.data));
      return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const StaffForm = ({ staff, onClose, onSuccess }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [clinics, setClinics] = useState([]);

  const handleIncrementAdded = async () => {
    // Refresh staff data to get updated salary
    if (staff && staff.id) {
      try {
        const response = await staffApi.getById(staff.id);
        const updatedStaff = response.data.data;
        // Update form data with new salary
        if (updatedStaff.salary_info) {
          setFormData(prev => ({
            ...prev,
            basic_salary: updatedStaff.salary_info.basic_salary || prev.basic_salary
          }));
        }
      } catch (error) {
        console.error('Failed to refresh staff data:', error);
      }
    }
    setRefreshKey(prev => prev + 1);
  };
  const [loading, setLoading] = useState(false);
  const [agreementFile, setAgreementFile] = useState(null);
  const [exitSettlement, setExitSettlement] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    date_of_birth: '',
    role_id: '',
    clinic_ids: [],
    is_active: true,
    basic_salary: '',
    allowances: '',
    deductions: '',
    join_date: '',
    resign_date: '',
    agreement_file: '',
    agreement_file_url: ''
  });

  // Fetch roles from backend once on mount
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await getCached('roles:list', () => rolesApi.getList());
        const list = response?.data?.data || [];
        // Exclude `admin` – staff form should not create admin users
        const filtered = list.filter(
          (r) => (r.name || '').toLowerCase() !== 'admin'
        );
        if (isMounted) setRoles(filtered);
      } catch (error) {
        console.error('Failed to load roles:', error);
        if (isMounted) toast.error('Failed to load roles');
      } finally {
        if (isMounted) setRolesLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await clinicsApi.getAll();
        if (!isMounted) return;
        setClinics(response?.data?.data || []);
      } catch {
        if (isMounted) setClinics([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (staff) {
      setFormData({
        email: staff.email || '',
        password: '', // Don't pre-fill password
        full_name: staff.full_name || '',
        phone: staff.phone || '',
        date_of_birth: staff.date_of_birth
          ? (typeof staff.date_of_birth === 'string'
            ? staff.date_of_birth.slice(0, 10)
            : new Date(staff.date_of_birth).toISOString().slice(0, 10))
          : '',
        role_id: staff.role_id != null ? String(staff.role_id) : '',
        is_active: staff.is_active !== undefined ? staff.is_active : true,
        basic_salary: staff.salary_info?.basic_salary || '',
        allowances: staff.salary_info?.allowances || '',
        deductions: staff.salary_info?.deductions || '',
        join_date: staff.join_date ? String(staff.join_date).slice(0, 10) : '',
        resign_date: staff.resign_date ? String(staff.resign_date).slice(0, 10) : '',
        agreement_file: staff.agreement_file || '',
        agreement_file_url: staff.agreement_file_url || '',
        clinic_ids: parseClinicIds(staff.clinic_ids).length > 0
          ? parseClinicIds(staff.clinic_ids)
          : parseClinicIds(staff.clinic_id)
      });
      setAgreementFile(null);
    }
  }, [staff]);

  useEffect(() => {
    if (!staff?.id || formData.is_active !== false) {
      setExitSettlement(null);
      return;
    }
    staffApi.getExitLeaveSettlement(staff.id)
      .then((res) => setExitSettlement(res.data?.data || null))
      .catch(() => setExitSettlement(null));
  }, [staff?.id, formData.is_active]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleClinicId = (clinicId) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.clinic_ids) ? prev.clinic_ids : [];
      const exists = current.includes(clinicId);
      return {
        ...prev,
        clinic_ids: exists
          ? current.filter((id) => id !== clinicId)
          : [...current, clinicId]
      };
    });
  };

  const handleRemoveResignDate = async () => {
    if (!staff?.id || !formData.resign_date) return;
    if (
      !window.confirm(
        'Remove resign date for this staff member? The 2-month notice period will end and they can submit leave requests again.'
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await staffApi.update(staff.id, {
        ...formData,
        resign_date: '',
        clinic_ids: (formData.clinic_ids || []).map((id) => Number(id)),
      });
      const refreshed = await staffApi.getById(staff.id);
      const updatedStaff = refreshed.data?.data;
      setFormData((prev) => ({ ...prev, resign_date: '' }));
      toast.success('Resign date removed');
      onSuccess(updatedStaff ? { staff: updatedStaff } : undefined);
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if the event came from a nested form (like salary increment form)
    if (e.target !== e.currentTarget && e.target.closest('.salary-increment-form')) {
      return; // Let the nested form handle its own submission
    }
    
    setLoading(true);

    try {
      let staffId = staff?.id;
      let updatedStaff = null;
      let patientsUnassigned = 0;

      if (staff) {
        const updateRes = await staffApi.update(staff.id, {
          ...formData,
          clinic_ids: (formData.clinic_ids || []).map((id) => Number(id))
        });
        staffId = staff.id;
        updatedStaff = updateRes?.data?.data || null;
        patientsUnassigned = Number(updatedStaff?.patients_unassigned) || 0;
      } else {
        // Create new staff
        if (!formData.password) {
          toast.error('Password is required for new staff');
          setLoading(false);
          return;
        }
        const createRes = await staffApi.create({
          ...formData,
          clinic_ids: (formData.clinic_ids || []).map((id) => Number(id))
        });
        staffId = createRes?.data?.data?.id;
        updatedStaff = createRes?.data?.data || null;
      }

      if (agreementFile && staffId) {
        const fd = new FormData();
        fd.append('agreement', agreementFile);
        await staffApi.uploadAgreement(staffId, fd);
      }
      toast.success(staff ? 'Staff updated successfully' : 'Staff created successfully');
      if (patientsUnassigned > 0) {
        toast.success(
          `Removed from ${patientsUnassigned} patient therapist assignment${patientsUnassigned === 1 ? '' : 's'}`
        );
      }
      onSuccess(updatedStaff ? { staff: updatedStaff } : undefined);
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAgreementFile(file);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {staff ? 'Edit Staff Member' : 'Add New Staff Member'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of birth
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                max={todayYmdIST()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                required
                disabled={rolesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {rolesLoading ? 'Loading roles...' : 'Select Role'}
                </option>
                {roles.map((role) => (
                  <option key={role.id} value={String(role.id)}>
                    {formatRoleLabel(role.name)}
                  </option>
                ))}
              </select>
            </div>

            {!staff && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <HiOutlineEyeOff className="w-5 h-5" />
                    ) : (
                      <HiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-gray-700">Active</label>
              </div>
              {staff && formData.is_active === false && (
                <p className="text-xs text-amber-700 mt-1">
                  Inactive staff will be removed from all assigned patients on save.
                </p>
              )}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic Access
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {clinics.length === 0 ? (
                  <p className="text-sm text-gray-500">No clinics found.</p>
                ) : (
                  clinics.map((clinic) => {
                    const clinicId = String(clinic.id);
                    const checked = (formData.clinic_ids || []).includes(clinicId);
                    return (
                      <label key={clinic.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleClinicId(clinicId)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{clinic.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Tick clinics to grant access.</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Leave &amp; Joining</h4>
            <p className="text-xs text-gray-500 mb-3">
              Join date drives probation (no usable monthly leave for the first 2 months). Saving join
              date recalculates leave history automatically. To set leave carry-forward balances, use{' '}
              <strong>Configuration → Staff leave opening setup</strong>.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Join Date</label>
                <input
                  type="date"
                  name="join_date"
                  value={formData.join_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resign Date</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    name="resign_date"
                    value={formData.resign_date}
                    onChange={handleChange}
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {staff && formData.resign_date && (
                    <button
                      type="button"
                      onClick={handleRemoveResignDate}
                      disabled={loading}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 disabled:opacity-50"
                      title="Remove resign date"
                    >
                      <HiOutlineTrash className="w-4 h-4" />
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Date staff resigned. For the next 2 months (notice period), they cannot submit leave requests.
                  {staff && formData.resign_date
                    ? ' Use Remove to clear resign date and allow leave again.'
                    : ''}
                </p>
              </div>
            </div>
            {staff && staff.exit_leave_accrual != null && (
              <p className="mt-2 text-sm text-gray-600">
                Exit leave accrual (probation payout):{' '}
                <span className="font-semibold">{parseFloat(staff.exit_leave_accrual || 0).toFixed(1)} days</span>
              </p>
            )}
            {exitSettlement && exitSettlement.payout_amount > 0 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <p className="font-semibold">Exit leave settlement preview</p>
                <p className="mt-1">
                  {parseFloat(exitSettlement.exit_leave_accrual || 0).toFixed(1)} days × ₹
                  {parseFloat(exitSettlement.per_day_salary || 0).toFixed(2)} ={' '}
                  <strong>₹{parseFloat(exitSettlement.payout_amount || 0).toFixed(2)}</strong>
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Salary Information</h4>
            <p className="text-xs text-gray-500 mb-3">
              Set default salary for this staff member. This will be used when generating payroll.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Basic Salary (₹)
                </label>
                <input
                  type="number"
                  name="basic_salary"
                  value={formData.basic_salary}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter basic salary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowances (₹)
                </label>
                <input
                  type="number"
                  name="allowances"
                  value={formData.allowances}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deductions (₹)
                </label>
                <input
                  type="number"
                  name="deductions"
                  value={formData.deductions}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Agreement Document</h4>
            <p className="text-xs text-gray-500 mb-3">
              Upload agreement file (PDF/DOC/DOCX). If you upload a new file, it will replace the old agreement.
            </p>
            <div className="space-y-2">
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleAgreementChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              {agreementFile ? (
                <p className="text-xs text-blue-700">Selected: {agreementFile.name}</p>
              ) : null}
              {!agreementFile && formData.agreement_file_url ? (
                <a
                  href={formData.agreement_file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary-700 hover:text-primary-800 underline"
                >
                  View current agreement
                </a>
              ) : null}
            </div>
          </div>

          {/* Salary Increment History - Only show when editing existing staff */}
          {staff && (staff.id || staff.user_id) && (
            <div 
              className="border-t border-gray-200 pt-4 mt-4"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <SalaryIncrements 
                key={refreshKey}
                userId={staff.id || staff.user_id} 
                staffName={staff.full_name || formData.full_name}
                onIncrementAdded={handleIncrementAdded}
                onCloseModal={onClose}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : staff ? 'Update Staff' : 'Create Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffForm;

